import { GUEST_KEY } from '../../presentation/preferences';
import { prepareImageUpload } from '../../utils/imageUpload';
import { unregisterNativePush } from '../pushNotificationService';
import i18n from '../../i18n';
import { apiErrorFromResponse } from '../../utils/apiErrors';
import { normalizeCategoryProgresses } from '../../utils/categoryKeys';
import { apiFetch, getApiBaseUrl } from './client';

export async function registerUser(userData) {
  return await apiFetch('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ ...userData, presentationMode: JSON.parse(localStorage.getItem(GUEST_KEY) || 'null')?.preference || undefined, language: i18n.resolvedLanguage || i18n.language }),
  });
}

export async function confirmEmail(token) {
  return apiFetch('/auth/confirm-email', { method: 'POST', body: JSON.stringify({ token }) });
}

export async function resendConfirmation(email) {
  return apiFetch('/auth/resend-confirmation', { method: 'POST', body: JSON.stringify({ email, language: i18n.resolvedLanguage || i18n.language }) });
}

export async function loginUser(credentials) {
  const data = await apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  if (data && data.token) {
    localStorage.setItem('goodvale_token', data.token);
    localStorage.setItem('goodvale_user', JSON.stringify(data));
    window.dispatchEvent(new Event('goodvale-auth-change'));
  }
  return data;
}

export async function forgotPassword(email) {
  return await apiFetch('/auth/forgot-password', {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

export async function resetPassword(token, newPassword) {
  return await apiFetch('/auth/reset-password', {
    method: 'POST',
    body: JSON.stringify({ token, newPassword }),
  });
}

export function getCurrentUser() {
  const userStr = localStorage.getItem('goodvale_user');
  return userStr ? JSON.parse(userStr) : null;
}

export async function getCurrentUserProfile() {
  const profile = await apiFetch('/auth/me');
  return profile ? { ...profile, categoryProgresses: normalizeCategoryProgresses(profile.categoryProgresses) } : profile;
}

export function selectCategoryTitle(categoryKey = null, level = null) {
  return apiFetch('/users/me/category-title', { method: 'PUT', body: JSON.stringify({ categoryKey, level }) });
}

export function getCategoryMilestoneUnlocks() {
  return apiFetch('/users/me/category-milestone-unlocks');
}

export async function uploadAvatar(imageBlob, fileName = 'avatar.webp') {
  const image = await prepareImageUpload(imageBlob, { maxDimension: 512 });
  const formData = new FormData();
  formData.append('file', image, image === imageBlob ? fileName : image.name);

  return await apiFetch('/users/avatar', {
    method: 'POST',
    body: formData,
  });
}

export async function uploadBanner(imageFile) {
  const image = await prepareImageUpload(imageFile, { maxDimension: 1920 });
  const formData = new FormData();
  formData.append('file', image, image.name || 'banner.webp');
  return apiFetch('/users/banner', { method: 'POST', body: formData });
}

export async function resetBanner() {
  return apiFetch('/users/banner', { method: 'DELETE' });
}

export async function changeHeroClass(heroClass) {
  const data = await apiFetch('/users/class', { method: 'POST', body: JSON.stringify({ heroClass }) });
  if (data?.token) {
    localStorage.setItem('goodvale_token', data.token);
    localStorage.setItem('goodvale_user', JSON.stringify(data));
    window.dispatchEvent(new Event('goodvale-auth-change'));
    window.dispatchEvent(new Event('storage'));
  }
  return data;
}

export async function updateUserProfileBio(bio) {
  return await apiFetch('/users/profile', {
    method: 'PUT',
    body: JSON.stringify({ bio }),
  });
}

export async function updateDirectMessagePermission(directMessagePermission) {
  return apiFetch('/users/profile', {
    method: 'PUT',
    body: JSON.stringify({ directMessagePermission }),
  });
}

export async function updateLevelUpPopupPreference(enabled) {
  const result = await apiFetch('/users/profile', {
    method: 'PUT',
    body: JSON.stringify({ levelUpPopupsEnabled: enabled }),
  });
  window.dispatchEvent(new CustomEvent('level-up-preference', { detail: enabled }));
  return result;
}

export async function updateHeroicApprovalPopupPreference(enabled) {
  const result = await apiFetch('/users/profile', {
    method: 'PUT',
    body: JSON.stringify({ heroicApprovalPopupsEnabled: enabled }),
  });
  window.dispatchEvent(new CustomEvent('heroic-approval-preference', { detail: enabled }));
  return result;
}

export async function changePassword({ currentPassword, newPassword }) {
  const data = await apiFetch('/auth/change-password', {
    method: 'POST',
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  if (data?.token) {
    localStorage.setItem('goodvale_token', data.token);
    localStorage.setItem('goodvale_user', JSON.stringify(data));
    window.dispatchEvent(new Event('goodvale-auth-change'));
  }
  return data;
}

export async function exportMyData() {
  const token = localStorage.getItem('goodvale_token');
  const response = await fetch(`${getApiBaseUrl()}/users/me/export`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw await apiErrorFromResponse(response, i18n);
  return response.blob();
}

export async function deleteMyAccount() {
  return apiFetch('/users/me', { method: 'DELETE' });
}

export async function getPublicUserProfile(userId) {
  return await apiFetch(`/users/public-profile/${userId}`);
}


/**
 * Re-issues a fresh JWT for the currently authenticated user.
 * Call this after role-changing operations (e.g. guild creation) so the
 * ProtectedRoute sees the updated role without requiring a full re-login.
 */
export async function refreshAuthToken() {
  const data = await apiFetch('/auth/refresh-token', { method: 'POST' });
  if (data && data.token) {
    localStorage.setItem('goodvale_token', data.token);
    localStorage.setItem('goodvale_user', JSON.stringify(data));
    window.dispatchEvent(new Event('goodvale-auth-change'));
    // Notify other tabs / useCurrentUser hook
    window.dispatchEvent(new Event('storage'));
  }
  return data;
}

export async function logoutUser() {
  try { await unregisterNativePush({ forceLocal: true }); } catch { /* Logout still clears the session when the device is offline. */ }
  localStorage.removeItem('goodvale_token');
  localStorage.removeItem('goodvale_user');
  window.dispatchEvent(new Event('goodvale-auth-change'));
}
